// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowLeft from "@keller/XDL/Icons/ArrowLeft";
import ArrowRight from "@keller/XDL/Icons/ArrowRight";
import { color, xScreen } from "@keller/XDL/Utilities";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color";
        image: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
        // XNG-Changes
        openInNewTab: boolean;
        showFadeEffect: boolean;
    };
}

const enum fields {
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    unAuthenticatedSlides = "unAuthenticatedSlides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    // XNG-Changes
    isAuthenticated = "isAuthenticated",
    authenticatedSlides = "authenticatedSlides",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        // XNG-Changes
        [fields.isAuthenticated]: boolean;
        [fields.authenticatedSlides]: SlideModel[];
        [fields.unAuthenticatedSlides]: SlideModel[];
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    // XNG-Changes
    container?: GridContainerProps;
    extension?: InjectableCss;
    actionsWrapper?: InjectableCss;
    slideIconProps?: IconPresentationProps;
    slideClickable?: ClickablePresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            position: relative;
        `,
    },
    iconProps: {
        size: 19,
        css: css`
            &&&& {
                color: ${color("text.link", 0.33)};
            }
        `,
    },
    slideIconProps: {
        size: 19,
        css: css`
            margin-left: 10px;
            &&&& {
                color: ${color("text.link", 0.33)};
            }
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            background-repeat: no-repeat;
            max-width: 1366px;
            margin: auto;
            min-height: 25vh;
            ${xScreen("lte", "md")} {
                min-height: auto;
                margin: unset;

                &:before,
                &:after {
                    display: none;
                }
            }
            ${xScreen("lte", "sm")} {
                ${({ isAuthenticated }: any) =>
                    isAuthenticated &&
                    css`
                        padding-top: 120px;
                        flex-direction: column;
                        min-height: 360px;
                        background-size: contain;
                    `};
            }
            ${xScreen("lte", "xs")} {
                ${({ isAuthenticated }: any) =>
                    isAuthenticated &&
                    css`
                        padding-top: 92px;
                        min-height: 92px;
                        background-size: contain;
                    `};
            }
            ${({ backgroundColor, showFadeEffect }: any) =>
                showFadeEffect &&
                css`
                    &:before {
                        content: "";
                        position: absolute;
                        left: 0;
                        height: 100%;
                        width: 185px;
                        background: linear-gradient(0.25turn, ${backgroundColor}, 60%, ${color("common.ui16", 0)});
                    }
                    &:after {
                        content: "";
                        position: absolute;
                        right: -30px;
                        height: 100%;
                        width: 185px;
                        background: linear-gradient(
                            0.25turn,
                            ${color("common.ui16", 0)},
                            30%,
                            ${color("common.ui16", 0.5)},
                            60%,
                            ${backgroundColor}
                        );
                    }
                `};
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
            min-height: 25vh;
            ${xScreen("lte", "md")} {
                min-height: 100%;
            }
        `,
    },
    slideClickable: {
        css: css`
            display: inline-block;
        `,
    },
    slideButton: {
        sizeVariant: "large",
        css: css`
            width: fit-content;
            font-size: 16px;
            display: inline-flex;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    dotButton: {
        css: css`
            height: 10px;
            width: 10px;
            padding: 0;
            margin: 0 4px;
            border-radius: 50%;
            background: ${color("common.white")};
            min-height: initial;
            border: 1px solid ${color("common.ui6")};

            &:hover,
            &:active {
                &&&& {
                    background: ${color("common.ui16")};
                    outline: none;
                }
            }
            &:focus:not(:disabled),
            &:active:not(:disabled) {
                outline: none;
                background: unset;
                border: 1px solid ${color("common.ui6")};
            }
        `,
    },
    container: {
        gap: 0,
        offsetCss: css`
            height: 100%;
        `,
    },
    extension: {
        css: css`
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    subheadingText: {
        css: css`
            display: block;
            margin-bottom: 24px;
            ${TypographyStyle} {
                margin: 0;
            }
        `,
    },
    actionsWrapper: {
        css: css`
            margin: 35px 20px 0;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
        `,
    },
};

export const Slideshow = ({ fields, extendedStyles }: OwnProps) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    // <XNG-Changes>
    // const { links: buttonLinks, hasMissingCategories } = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const isAuthenticatedField = fields.isAuthenticated;
    const slides = isAuthenticatedField ? fields.authenticatedSlides : fields.unAuthenticatedSlides;
    const { links: buttonLinks, hasMissingCategories } = useGetLinks(slides ?? [], o => o.fields.buttonLink);
    // </XNG-Changes>
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !slides || slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, slides]);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    if (hasMissingCategories) {
        return null;
    }

    if (!slides || slides.length === 0) {
        return null;
    }

    let heightStyles: string;
    // switch (fields.height) {
    //     case "1/4 viewport":
    //         heightStyles = "min-height: 25vh;";
    //         break;
    //     case "1/2 viewport":
    //         heightStyles = "min-height: 50vh;";
    //         break;
    //     case "3/4 viewport":
    //         heightStyles = "min-height: 75vh;";
    //         break;
    //     case "fullViewport":
    //         heightStyles = "min-height: 100vh;";
    //         break;
    // }

    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    return (
        <StyledWrapper {...styles.slideshowWrapper}>
            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                <StyledWrapper {...styles.slideContainerWrapper}>
                    {slides.map((slide, index) => {
                        const buttonLink = buttonLinks[index];

                        let responsiveImageBehaviorStyles;
                        if (slide.fields.responsiveImageBehavior === "cover") {
                            responsiveImageBehaviorStyles = "background-size: cover;";
                        } else if (slide.fields.responsiveImageBehavior === "center") {
                            responsiveImageBehaviorStyles = "background-size: contain;";
                        } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                            responsiveImageBehaviorStyles = "background-size: auto 100%;";
                        } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                            responsiveImageBehaviorStyles = "background-size: 100% auto;";
                        }

                        const backgroundStyles =
                            slide.fields.background === "image"
                                ? `background-image: url(${parseImageUrl(slide.fields.image).url});`
                                : `background-color: ${slide.fields.backgroundColor};`;
                        const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

                        let overlayPositioningStyles;
                        if (!slide.fields.partialOverlay) {
                            overlayPositioningStyles = "align-items: stretch;";
                        } else if (slide.fields.partialOverlayPositioning === "top") {
                            overlayPositioningStyles = "align-items: flex-start;";
                        } else if (slide.fields.partialOverlayPositioning === "middle") {
                            overlayPositioningStyles = "align-items: center;";
                        } else if (slide.fields.partialOverlayPositioning === "bottom") {
                            overlayPositioningStyles = "align-items: flex-end;";
                        }

                        if (isAuthenticatedField) {
                            overlayPositioningStyles += "justify-content: flex-end";
                        }

                        let fontSizeStyles;
                        if (fields.responsiveFontSizes || fields.customFontSizes) {
                            fontSizeStyles = responsiveStyleRules(
                                fields.responsiveFontSizes,
                                fields.customFontSizes ? fields : undefined,
                            );
                        }

                        // XNG-Changes - Remove ${heightStyles}
                        const slideWrapperStyles = {
                            css: css`
                                ${styles.slideContentWrapper?.css || ""}
                                ${textAlignStyles}
                                ${backgroundStyles}
                                ${responsiveImageBehaviorStyles}
                                ${focalPointStyles}
                                ${overlayPositioningStyles}
                                ${fontSizeStyles}
                            `,
                        };
                        const slideOverlayWrapperStyles = {
                            css: css`
                                ${styles.slideOverlayWrapper?.css || ""}
                                background-color: ${slide.fields.background === "image"
                                    ? slide.fields.imageOverlay
                                    : ""};
                                padding: ${slide.fields.contentPadding}px;
                                ${isAuthenticatedField &&
                                css`
                                    ${xScreen("gt", "sm")} {
                                        max-width: 566px;
                                    }
                                `}
                            `,
                        };
                        const innerWrapper = {
                            css: css`
                                &,
                                & > div {
                                    height: 100%;
                                    z-index: 1;
                                }
                            `,
                        };
                        const slideItem = {
                            css: css`
                                ${isAuthenticatedField ? "flex-direction: row-reverse" : ""};
                                height: 100%;
                            `,
                        };
                        const outerWrapper = {
                            css: css`
                                width: 100%;
                                background-color: ${slide.fields.backgroundColor};
                                display: flex;
                                flex: 0 0 100%;
                                position: relative;
                                /* Chrome issue: description - https://xngage.atlassian.net/browse/KSC-937?focusedCommentId=213404 */
                                box-shadow: 1px 0 0 ${slide.fields.backgroundColor};
                            `,
                        };
                        const elementProps = {
                            showFadeEffect: slide.fields.showFadeEffect,
                            backgroundColor: slide.fields.backgroundColor,
                            isAuthenticated: isAuthenticatedField,
                        };
                        return (
                            <StyledWrapper key={index} {...outerWrapper}>
                                <StyledWrapper {...slideWrapperStyles} {...elementProps}>
                                    <StyledWrapper {...innerWrapper}>
                                        <GridContainer {...styles.container}>
                                            <GridItem
                                                {...slideItem}
                                                width={isAuthenticatedField ? [12, 12, 12, 12, 12] : [12, 12, 8, 7, 7]}
                                            >
                                                <StyledWrapper {...slideOverlayWrapperStyles}>
                                                    {slide.fields.heading && (
                                                        <Typography {...styles.headingText}>
                                                            {parse(slide.fields.heading, parserOptions)}
                                                        </Typography>
                                                    )}
                                                    {slide.fields.subheading && (
                                                        <Typography {...styles.subheadingText}>
                                                            {parse(slide.fields.subheading, parserOptions)}
                                                        </Typography>
                                                    )}
                                                    {(slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                                        <Clickable
                                                            {...styles.slideClickable}
                                                            target={slide.fields.openInNewTab ? "_blank" : undefined}
                                                            href={buttonLink?.url}
                                                        >
                                                            <Button
                                                                mergeCss={true}
                                                                {...styles.slideButton}
                                                                variant={slide.fields.buttonVariant}
                                                            >
                                                                {slide.fields.buttonLabel ||
                                                                    buttonLink?.title ||
                                                                    buttonLink?.url}
                                                                {!isAuthenticatedField && (
                                                                    <ButtonIcon
                                                                        {...styles.slideIconProps}
                                                                        src={ArrowRight}
                                                                    />
                                                                )}
                                                            </Button>
                                                        </Clickable>
                                                    )}
                                                </StyledWrapper>
                                            </GridItem>
                                            {!isAuthenticatedField && (
                                                <GridItem {...styles.extension} width={[12, 12, 4, 5, 5]} />
                                            )}
                                        </GridContainer>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.actionsWrapper}>
                {fields.showArrows && slides.length > 1 && (
                    <StyledWrapper {...styles.prevArrowWrapper}>
                        <Clickable
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            aria-label={translate("Previous")}
                        >
                            <Icon {...styles.iconProps} src={ArrowLeft} />
                        </Clickable>
                    </StyledWrapper>
                )}

                {fields.slideIndicator && slides.length > 1 && (
                    <StyledWrapper {...styles.dotsContainerWrapper}>
                        {slides.map((_, i) => {
                            const dotColorStyles =
                                i === selectedIndex
                                    ? css`
                                          &&& {
                                              background: ${color("text.main")};
                                              border: 0;
                                              &:hover {
                                                  background: ${color("common.white")};
                                              }
                                          }
                                      `
                                    : "";
                            const dotButtonStyles = {
                                css: css`
                                    ${styles.dotButton?.css || ""}
                                    ${dotColorStyles}
                                `,
                            };

                            return (
                                <Button
                                    // XNG-Changes
                                    // className="slide-dot-button"
                                    key={i}
                                    {...dotButtonStyles}
                                    onClick={() => embla && embla.scrollTo(i)}
                                    aria-label={translate("Scroll to {0}", `${i + 1}`)}
                                />
                            );
                        })}
                    </StyledWrapper>
                )}
                {fields.showArrows && slides.length > 1 && (
                    <StyledWrapper {...styles.nextArrowWrapper}>
                        {/* XNG-Changes - Replace Button with Clickable */}
                        <Clickable
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            aria-label={translate("Next")}
                        >
                            <Icon {...styles.iconProps} src={ArrowRight} />
                        </Clickable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
                isVisible: widget => false,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
                isVisible: widget => false,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
                isVisible: widget => false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.isAuthenticated,
                displayName: "Authoring for Authenticated mode",
                editorTemplate: "CheckboxField",
                variant: "toggle",
                defaultValue: false,
                fieldType: "General",
                tab: contentTab,
            },
            {
                name: fields.authenticatedSlides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                isVisible: item => item.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                        isVisible: widget => false,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Text Background Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "#FFFFFF",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Background Color for Widget",
                        defaultValue: "#FFFFFF",
                    },
                    {
                        name: "showFadeEffect",
                        displayName: "Show Fade Effect On The Slide Image",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.backgroundColor,
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 35,
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open in new tap",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.unAuthenticatedSlides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                isVisible: item => !item?.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                        isVisible: widget => false,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Text Background Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "rgba(0, 0, 0, 0.75)",
                    },
                    {
                        name: "partialOverlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => false,
                    },
                    {
                        name: "partialOverlayPositioning",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top", value: "top" },
                            { displayName: "Middle", value: "middle" },
                            { displayName: "Bottom", value: "bottom" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "bottom",
                        isVisible: widget => widget.fields.partialOverlay && false,
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Background Color for Widget",
                        defaultValue: "#333333",
                    },
                    {
                        name: "showFadeEffect",
                        displayName: "Show Fade Effect On The Slide Image",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.backgroundColor,
                    },
                    {
                        name: "focalPoint",
                        displayName: "Focal Point",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top Left", value: "topLeft" },
                            { displayName: "Top Center", value: "topCenter" },
                            { displayName: "Top Right", value: "topRight" },
                            { displayName: "Center Left", value: "centerLeft" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Center Right", value: "centerRight" },
                            { displayName: "Bottom Left", value: "bottomLeft" },
                            { displayName: "Bottom Center", value: "bottomCenter" },
                            { displayName: "Bottom Right", value: "bottomRight" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                        isVisible: widget => false,
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 35,
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => false,
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open in new tap",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
